import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { ToastComponent } from './toast.component';
import { ToastMessage, ToastPosition } from './toast.models';
import { ToastService } from './toast.service';

@Component({
    selector: 'pax-toast-container',
    standalone: true,
    imports: [LucideAngularModule, ToastComponent],
    templateUrl: './toast-container.component.html',
    animations: [
        trigger('toastAnimation', [
            state(
                'void',
                style({
                    transform: 'translate(0, -100%) scale(0.8)',
                    opacity: 0,
                    height: 0,
                    marginBottom: 0,
                }),
            ),
            state(
                'active',
                style({
                    transform: 'translate(0, 0) scale(1)',
                    opacity: 1,
                    height: '*',
                    marginBottom: '0.5rem', // 8px, matches the mb-2 class
                }),
            ),
            transition(
                'void => active',
                [
                    style({
                        transform: '{{transform}} scale(0.8)',
                        opacity: 0,
                        height: '*',
                        marginBottom: '0.5rem',
                    }),
                    animate('200ms cubic-bezier(0.0, 0.0, 0.2, 1)'),
                ],
                { params: { transform: 'translate(0, -100%)' } },
            ),
            transition(
                'active => void',
                [
                    animate(
                        '300ms cubic-bezier(0.4, 0.0, 1, 1)',
                        style({
                            transform: '{{transform}} scale(0.8)',
                            opacity: 0,
                            height: 0,
                            marginBottom: 0,
                        }),
                    ),
                ],
                { params: { transform: 'translate(0, -100%)' } },
            ),
        ]),
        trigger('progressBar', [
            state(
                'void',
                style({
                    width: '100%',
                }),
            ),
            state(
                'end',
                style({
                    width: '0%',
                }),
            ),
            transition('void => end', [animate('{{life}}ms linear')], {
                params: { life: '3000' },
            }),
        ]),
    ],
})
export class ToastContainerComponent {
    readonly positions: ToastPosition[] = ['top-left', 'top-center', 'top-right', 'bottom-left', 'bottom-center', 'bottom-right'];

    constructor(private toastService: ToastService) {}

    getToastsByPosition(position: ToastPosition): ToastMessage[] {
        return this.toastService.toasts$().filter((toast) => (toast.options?.position || 'top-right') === position);
    }

    removeToast(id: string): void {
        this.toastService.remove(id);
    }

    getTransform(position: ToastPosition): string {
        switch (position) {
            case 'top-left':
            case 'top-center':
            case 'top-right':
                return 'translate(0, -100%)';
            case 'bottom-left':
            case 'bottom-center':
            case 'bottom-right':
                return 'translate(0, 100%)';
            default:
                return 'translate(0, -100%)';
        }
    }

    getPositionClasses(position: ToastPosition): string {
        const baseClasses = 'pointer-events-none flex flex-col';
        switch (position) {
            case 'top-left':
                return `${baseClasses} top-4 left-4`;
            case 'top-center':
                return `${baseClasses} top-4 left-1/2 -translate-x-1/2`;
            case 'top-right':
                return `${baseClasses} top-4 right-4`;
            case 'bottom-left':
                return `${baseClasses} bottom-4 left-4`;
            case 'bottom-center':
                return `${baseClasses} bottom-4 left-1/2 -translate-x-1/2`;
            case 'bottom-right':
                return `${baseClasses} bottom-4 right-4`;
        }
    }
}
